import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { fetchPageViewStatistics } from '../../api/userInteractionReports';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1'];

/**
 * Component for displaying page view statistics
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 */
function PageViewsReport({ filters }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Load page view statistics when filters change
  useEffect(() => {
    const loadStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchPageViewStatistics(filters);
        setStatistics(data);
      } catch (error) {
        console.error('Error loading page view statistics:', error);
        setError('Failed to load page view statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [filters]);

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // If no data, show message
  if (!statistics || statistics.totalPageViews === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No page view data available for the selected filters.
      </Alert>
    );
  }

  // Prepare data for charts
  const pageViewsData = Object.entries(statistics.pageViewsByPage || {})
    .map(([page, count]) => ({ name: page, value: count }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10); // Top 10 pages

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Page View Statistics
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Total Page Views Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Page Views
            </Typography>
            <Typography variant="h4">
              {statistics.totalPageViews}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Viewed Page Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Viewed Page
            </Typography>
            <Typography variant="h5">
              {statistics.mostViewedPages?.[0]?.page || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {statistics.mostViewedPages?.[0]?.count || 0} views
            </Typography>
          </CardContent>
        </Card>
        
        {/* Average Views Per Page Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Average Views Per Page
            </Typography>
            <Typography variant="h5">
              {Object.keys(statistics.pageViewsByPage || {}).length > 0
                ? Math.round(statistics.totalPageViews / Object.keys(statistics.pageViewsByPage).length)
                : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Page Views by Day Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Page Views by Day
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={statistics.pageViewsByDayArray || []}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" name="Page Views" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {/* Top Pages Pie Chart */}
        <Paper sx={{ p: 2, flex: '1 1 300px' }}>
          <Typography variant="subtitle1" gutterBottom>
            Top Pages
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pageViewsData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name.substring(0, 15)}${name.length > 15 ? '...' : ''}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pageViewsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
      
      {/* Most Viewed Pages Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Most Viewed Pages
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="most viewed pages table">
            <TableHead>
              <TableRow>
                <TableCell>Page</TableCell>
                <TableCell align="right">Views</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(statistics.mostViewedPages || []).map((page) => (
                <TableRow
                  key={page.page}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {page.page}
                  </TableCell>
                  <TableCell align="right">{page.count}</TableCell>
                  <TableCell align="right">
                    {((page.count / statistics.totalPageViews) * 100).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* User Activity Table */}
      {Object.keys(statistics.pageViewsByUser || {}).length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            User Activity
          </Typography>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="user activity table">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell align="right">Page Views</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(statistics.pageViewsByUser || {})
                  .map(([userId, count]) => ({ userId, count }))
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 10) // Top 10 users
                  .map((user) => (
                    <TableRow
                      key={user.userId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {user.userId === '00000000-0000-0000-0000-000000000000' 
                          ? 'Anonymous' 
                          : user.userId.substring(0, 8) + '...'}
                      </TableCell>
                      <TableCell align="right">{user.count}</TableCell>
                      <TableCell align="right">
                        {((user.count / statistics.totalPageViews) * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default PageViewsReport;
